import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import * as offerService from '../../services/OfferServices/offerService';
import type {
  CandidateOffer,
  OfferActivityLog,
  CreateOfferPayload,
  DeclineOfferPayload,
} from '../../services/OfferServices/offerService';

interface OfferState {
  offers: CandidateOffer[];
  history: OfferActivityLog[];
  loading: boolean;
  submitting: boolean;
  error: string | null;
}

const initialState: OfferState = {
  offers: [],
  history: [],
  loading: false,
  submitting: false,
  error: null,
};

// ── Thunks ────────────────────────────────────────────────────────────────────

export const fetchOffersByCandidate = createAsyncThunk(
  'offers/fetchByCandidate',
  async (candidateId: number, { rejectWithValue }) => {
    try {
      const result = await offerService.getOffersByCandidate(candidateId);
      // Normalize: API may return { data: [...] } or a plain array
      if (Array.isArray(result)) return result;
      if (result && Array.isArray((result as any).data)) return (result as any).data;
      return [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || 'Failed to fetch offers');
    }
  },
);

export const fetchOfferHistory = createAsyncThunk(
  'offers/fetchHistory',
  async (offerId: number, { rejectWithValue }) => {
    try {
      return await offerService.getOfferHistory(offerId);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || 'Failed to fetch offer history');
    }
  },
);

export const createOffer = createAsyncThunk(
  'offers/create',
  async (payload: CreateOfferPayload, { rejectWithValue }) => {
    try {
      return await offerService.createOffer(payload);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || 'Failed to create offer');
    }
  },
);

export const submitForApproval = createAsyncThunk(
  'offers/submit',
  async (offerId: number, { rejectWithValue }) => {
    try {
      return await offerService.submitForApproval(offerId);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || 'Failed to submit for approval');
    }
  },
);

export const approveOffer = createAsyncThunk(
  'offers/approve',
  async (offerId: number, { rejectWithValue }) => {
    try {
      return await offerService.approveOffer(offerId);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || 'Failed to approve offer');
    }
  },
);

export const sendOffer = createAsyncThunk(
  'offers/send',
  async (offerId: number, { rejectWithValue }) => {
    try {
      return await offerService.sendOffer(offerId);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || 'Failed to send offer');
    }
  },
);

export const acceptOffer = createAsyncThunk(
  'offers/accept',
  async (offerId: number, { rejectWithValue }) => {
    try {
      return await offerService.acceptOffer(offerId);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || 'Failed to accept offer');
    }
  },
);

export const declineOffer = createAsyncThunk(
  'offers/decline',
  async ({ offerId, payload }: { offerId: number; payload: DeclineOfferPayload }, { rejectWithValue }) => {
    try {
      return await offerService.declineOffer(offerId, payload);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || 'Failed to process decline');
    }
  },
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const offerSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    clearOffers: (state) => {
      state.offers = [];
      state.history = [];
      state.error = null;
    },
    updateOfferInList: (state, action: PayloadAction<CandidateOffer>) => {
      const idx = state.offers.findIndex(o => o.offer_id === action.payload.offer_id);
      if (idx !== -1) state.offers[idx] = action.payload;
    },
  },
  extraReducers: (builder) => {
    // fetchOffersByCandidate
    builder
      .addCase(fetchOffersByCandidate.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchOffersByCandidate.fulfilled, (state, action) => {
        state.loading = false;
        // Always ensure offers is an array to prevent .map crashes
        state.offers = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchOffersByCandidate.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

    // fetchOfferHistory
    builder
      .addCase(fetchOfferHistory.pending, (state) => { state.loading = true; })
      .addCase(fetchOfferHistory.fulfilled, (state, action) => { state.loading = false; state.history = action.payload; })
      .addCase(fetchOfferHistory.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

    // createOffer
    builder
      .addCase(createOffer.pending, (state) => { state.submitting = true; state.error = null; })
      .addCase(createOffer.fulfilled, (state, action) => { state.submitting = false; state.offers.push(action.payload); })
      .addCase(createOffer.rejected, (state, action) => { state.submitting = false; state.error = action.payload as string; });

    // Shared updater for status-changing thunks
    const updateOfferStatus = (state: OfferState, action: PayloadAction<CandidateOffer>) => {
      state.submitting = false;
      const idx = state.offers.findIndex(o => o.offer_id === action.payload.offer_id);
      if (idx !== -1) state.offers[idx] = action.payload;
      else state.offers.push(action.payload);
    };

    [submitForApproval, approveOffer, sendOffer, acceptOffer].forEach(thunk => {
      builder
        .addCase(thunk.pending, (state) => { state.submitting = true; state.error = null; })
        .addCase(thunk.fulfilled, updateOfferStatus)
        .addCase(thunk.rejected, (state, action) => { state.submitting = false; state.error = action.payload as string; });
    });

    // declineOffer — doesn't return an updated offer directly
    builder
      .addCase(declineOffer.pending, (state) => { state.submitting = true; state.error = null; })
      .addCase(declineOffer.fulfilled, (state) => { state.submitting = false; })
      .addCase(declineOffer.rejected, (state, action) => { state.submitting = false; state.error = action.payload as string; });
  },
});

export const { clearOffers, updateOfferInList } = offerSlice.actions;
export default offerSlice.reducer;
